import { ChangeDetectionStrategy, Component, inject } from "@angular/core";
import { Currency } from "@front/m19-api-client";
import { Metric } from "@front/m19-metrics";
import { CurrencyStat } from "@front/m19-models";
import { AdStatsType, MetricsSelectorKey } from "@front/m19-services";
import { MODAL_DATA, ModalOptions } from "@front/m19-ui";
import { AggregationFunction } from "@front/m19-utils";
import { Observable } from "rxjs";
import { ChartRendererComponent } from "./chart-renderer.component";
import { DataSetEventAnnotation } from "@front/m19-models/dataset";

@Component({
  template: ` <div class="flex h-full flex-col overflow-hidden">
    <app-chart-renderer
      class="min-h-0 flex-1"
      [locale]="modalData.locale"
      [currency]="modalData.currency"
      [annotations$]="modalData.annotations$"
      [dataSetConfiguration]="modalData.dataSetConfiguration"
      [chartData]="modalData.chartData$"
      [selectedMetrics]="modalData.selectedMetrics"
      [metrics]="modalData.metrics"
      [localStorageKey]="modalData.localStorageKey"
      [strategyTargetHistory]="modalData.strategyTargetHistory ?? false"
      [splitData]="modalData.splitData"
    ></app-chart-renderer>
  </div>`,
  imports: [ChartRendererComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChartModalComponent<T extends CurrencyStat> {
  static readonly defaultModalOptions: Omit<ModalOptions<unknown>, "data"> = { size: "xl", height: "fill" };
  readonly modalData = inject<ChartModalInputData<T>>(MODAL_DATA);
}

export interface ChartModalInputData<T> {
  locale: string;
  currency: Currency;
  annotations$?: Observable<DataSetEventAnnotation[]>;
  dataSetConfiguration: DataSetConfiguration<T>;
  chartData$: Observable<ChartData<T>>;
  selectedMetrics: Metric<T>[];
  metrics: Metric<T>[];
  localStorageKey: MetricsSelectorKey;
  strategyTargetHistory?: boolean;
  splitData?: { splitData1: T; splitData2: T; type: AdStatsType } | undefined;
}

export interface DataSetConfiguration<T> {
  aggregationFunction: AggregationFunction<any>[];
  aspectRatio?: number;
  metricsOnSameScale?: Metric<T>[][];
}

export type ChartData<T> = {
  data: T[];
  previousData?: T[];
  totalData: T;
  totalPreviousData?: T;
};
